// Calendar Sync settings — connect a coach's Google and Outlook calendars (Settings → Sessions). Renders status into
// #calendar-sync-content and opens a small window to start a connection.
//
// Outlook part 1 (docs/superpowers/specs/2026-10-29-outlook-calendar-design.md section 9; the prototype Clayton
// approved 10/30, _mockups/outlook-calendar-sync/): one block per account with its calendars, ONE place new events,
// client sessions and bookings go, and none of the controls he had removed (the busy-time choice, the blocking switch,
// "Refresh realtime sync", Apple).

import { callFunction } from './api.js';
import { showToast, escHtml, upgradeSelects, showConfirm } from './utils.js';

let _status = null;
let _initialized = false;
let _pollTimer = null;

const NAME = { google: 'Google Calendar', outlook: 'Outlook' };
const ORDER = ['google', 'outlook'];

function providerIcon(p) {
  if (p === 'google') return `<svg width="22" height="22" viewBox="0 0 48 48" aria-hidden="true"><path fill="#fbbc04" d="M11 33.5h7v-19h-7z"/><path fill="#34a853" d="M14.5 41h19v-7h-19z"/><path fill="#ea4335" d="M14.5 7h19v7h-19z"/><path fill="#1a73e8" d="M30 14.5h7v19h-7z"/><path fill="#34a853" d="M11 14.5h7L11 7z"/><path fill="#ea4335" d="M37 14.5L30 7v7.5z"/><path fill="#1a73e8" d="M30 33.5h7L30 41z"/><path fill="#fbbc04" d="M11 33.5L18 41v-7.5z"/></svg>`;
  if (p === 'outlook') return `<svg width="22" height="22" viewBox="0 0 48 48" aria-hidden="true"><rect x="5" y="9" width="38" height="32" rx="5" fill="#0a78d6"/><rect x="5" y="9" width="38" height="9" rx="5" fill="#0b5fae"/><g fill="#fff"><rect x="11" y="23" width="6" height="5" rx="1"/><rect x="21" y="23" width="6" height="5" rx="1"/><rect x="31" y="23" width="6" height="5" rx="1"/><rect x="11" y="31" width="6" height="5" rx="1"/><rect x="21" y="31" width="6" height="5" rx="1"/></g></svg>`;
  return '';
}

/** Can new events, sessions and bookings go on this calendar? The status says so per calendar. */
const writable = (c) => c && c.canEdit !== false && c.accessRole !== 'reader' && c.accessRole !== 'freeBusyReader';
const connections = () => ORDER.map((p) => (_status?.connections || []).find((c) => c.provider === p)).filter(Boolean);

// Both accounts take new events and Booked calls (Outlook part 2). Client sessions stay on Google until part 3, so the
// label does not name them.
const TAKES_NEW = ['google', 'outlook'];

/** Where new things go now, as the picker's value: `provider:calendarId`. */
function pickedNow() {
  const conns = connections().filter((c) => TAKES_NEW.includes(c.provider));
  const target = conns.find((c) => c.newEventsGoHere) || conns[0];
  if (!target) return '';
  const cals = (target.calendars || []).filter(writable);
  const cal = cals.find((c) => c.id === target.writeBackCalendarId) || cals.find((c) => c.primary) || cals[0];
  return cal ? `${target.provider}:${cal.id}` : '';
}

async function loadStatus() {
  try {
    _status = await callFunction('coach-calendar-status', {});
  } catch (e) {
    console.warn('[calendar-sync] status load failed', e);
    _status = { googleOAuthConfigured: false, microsoftOAuthConfigured: false, connections: [] };
  }
  render();
}

function accountBlock(conn) {
  const p = conn.provider;
  const cals = conn.calendars || [];
  const on = cals.filter((c) => c.syncEnabled !== false).length;
  const rows = cals.map((c) => `
    <label class="cs-cal-row" data-acct="${p}" data-cal-id="${escHtml(c.id)}">
      <input type="checkbox" ${c.syncEnabled !== false ? 'checked' : ''} />
      <span class="cs-cal-swatch" style="${c.color ? `background:${escHtml(c.color)};` : ''}"></span>
      <span class="cs-cal-name">${escHtml(c.summary || c.id)}${c.primary ? ' <small style="color:var(--text-muted);">· primary</small>' : ''}${writable(c) ? '' : ' <small style="color:var(--text-muted);">· view only</small>'}</span>
    </label>`).join('');
  return `
    <div class="cs-acct">
      <div class="cs-acct-head">
        ${providerIcon(p)}
        <div style="flex:1;min-width:0;">
          <div style="font-size:var(--fs-sm);font-weight:600;color:var(--text);overflow-wrap:anywhere;">${NAME[p]} · ${escHtml(conn.email || '')}</div>
          <div style="font-size:var(--fs-xs);color:var(--text-muted);">Connected. ${on} of ${cals.length} calendar${cals.length === 1 ? '' : 's'} on.</div>
        </div>
        <button class="btn-ghost btn-sm" data-cs-disconnect="${p}">Disconnect</button>
      </div>
      <div class="cs-cal-list">${rows}</div>
    </div>`;
}

function connectRow(p) {
  return `
    <div class="cs-acct cs-acct-head">
      ${providerIcon(p)}
      <div style="flex:1;">
        <div style="font-size:var(--fs-sm);font-weight:600;color:var(--text);">${NAME[p]}</div>
        <div style="font-size:var(--fs-xs);color:var(--text-muted);">Not connected</div>
      </div>
      <button class="btn-primary" data-cs-connect="${p}">Connect</button>
    </div>`;
}

function render() {
  const root = document.getElementById('calendar-sync-content');
  if (!root || !_status) return;

  const conns = connections();
  const offer = ORDER.filter((p) => !conns.some((c) => c.provider === p))
    .filter((p) => (p === 'google' ? _status.googleOAuthConfigured : _status.microsoftOAuthConfigured));

  if (!conns.length && !offer.length) {
    root.innerHTML = `<p class="settings-note">Calendar connections aren't set up on this server yet. The keys go in FitOps Admin.</p>`;
    return;
  }

  let shared = '';
  if (conns.length) {
    const groups = conns.filter((c) => TAKES_NEW.includes(c.provider)).map((c) => {
      const opts = (c.calendars || []).filter(writable)
        .map((cal) => `<option value="${c.provider}:${escHtml(cal.id)}">${escHtml(cal.summary || cal.id)}</option>`).join('');
      return opts ? `<optgroup label="${NAME[c.provider]} · ${escHtml(c.email || '')}">${opts}</optgroup>` : '';
    }).join('');
    shared = `${groups ? `
      <div style="margin-top:18px;max-width:360px;">
        <label for="cs-write-back" style="font-size:var(--fs-xs);color:var(--text);font-weight:500;display:block;margin-bottom:6px;">Add new events and bookings to</label>
        <select id="cs-write-back" class="crm-select cs-select">${groups}</select>
      </div>` : ''}
      <div class="settings-actions" style="display:flex;gap:8px;align-items:center;">
        <span style="flex:1;"></span>
        <button class="btn-primary" id="cs-save-prefs">Save preferences</button>
      </div>`;
  }

  root.innerHTML = `<div class="cs-accts">${conns.map(accountBlock).join('')}${offer.map(connectRow).join('')}</div>${shared}`;
  const picker = document.getElementById('cs-write-back');
  if (picker) picker.value = pickedNow();
  upgradeSelects(root);

  root.querySelectorAll('[data-cs-connect]').forEach((b) => b.addEventListener('click', () => startWizard()));
  root.querySelectorAll('[data-cs-disconnect]').forEach((b) => b.addEventListener('click', () => disconnect(b.getAttribute('data-cs-disconnect'))));

  const saveBtn = document.getElementById('cs-save-prefs');
  saveBtn?.addEventListener('click', () => save(root, saveBtn));
  bindPrefsDirtyTracking(root, saveBtn);
}

async function disconnect(p) {
  const words = p === 'outlook'
    ? 'Disconnect Outlook? What is already on your calendar here stays. New events and bookings stop going to Outlook. To take FitOps Pro off your Microsoft account too, remove it at account.microsoft.com.'
    : 'Disconnect Google Calendar? What is already on your calendar here stays. New events and bookings stop going to Google.';
  if (!(await showConfirm(words, { okLabel: 'Disconnect', danger: true }))) return;
  try {
    await callFunction('coach-calendar-disconnect', { provider: p });
    showToast('Disconnected.', 'success');
    await loadStatus();
  } catch (e) {
    showToast(e.message || 'Failed to disconnect', 'error');
  }
}

/** One save per account that changed: its calendars, and where new things go when it is the one picked. */
async function save(root, saveBtn) {
  const picked = document.getElementById('cs-write-back')?.value || '';
  const [pickedProvider, pickedCal] = picked.split(/:(.*)/s);
  const saves = [];
  for (const conn of connections()) {
    const p = conn.provider;
    const rows = Array.from(root.querySelectorAll(`.cs-cal-row[data-acct="${p}"]`)).map((row) => ({
      id: row.dataset.calId, syncEnabled: row.querySelector('input[type="checkbox"]').checked,
    }));
    const changed = rows.some((r) => {
      const was = (conn.calendars || []).find((c) => c.id === r.id);
      return !was || (was.syncEnabled !== false) !== r.syncEnabled;
    });
    const body = { provider: p };
    if (changed) body.calendars = rows;
    if (picked && picked !== pickedNow() && pickedProvider === p) { body.writeBackCalendarId = pickedCal; body.newEventsGoHere = true; }
    if (Object.keys(body).length > 1) saves.push(body);
  }
  saveBtn.disabled = true;
  saveBtn.textContent = 'Saving…';
  try {
    for (const body of saves) await callFunction('coach-calendar-update-prefs', body);
    showToast('Calendar preferences saved.', 'success');
    // Re-render from the stored values; the fresh button starts greyed out.
    await loadStatus();
  } catch (e) {
    showToast(e.message || 'Failed to save', 'error');
    saveBtn.textContent = 'Save preferences';
    saveBtn.disabled = false;
  }
}

// Same contract as every other Settings save button (initSettingsDirtyTracking
// in app.js): greyed out while the inputs match what is stored, alive the
// moment one differs. The panel re-renders itself after every load and save,
// so it is bound here rather than in app.js's static registry — the calendar
// rows have no fixed ids.
function bindPrefsDirtyTracking(root, btn) {
  if (!btn) return;
  const snap = () => {
    const cals = Array.from(root.querySelectorAll('.cs-cal-row input[type="checkbox"]')).map((el) => String(el.checked));
    return [...cals, document.getElementById('cs-write-back')?.value ?? ''].join('\0');
  };
  const clean = snap();
  btn.disabled = true;
  ['input', 'change'].forEach((evt) => root.addEventListener(evt, () => { btn.disabled = snap() === clean; }));
}

// ── Connect window ─────────────────────────────────────────────
function startWizard() {
  const conns = connections();
  const offer = ORDER.filter((p) => !conns.some((c) => c.provider === p))
    .filter((p) => (p === 'google' ? _status?.googleOAuthConfigured : _status?.microsoftOAuthConfigured));
  const overlay = document.createElement('div');
  overlay.className = 'cs-wizard-overlay';
  overlay.innerHTML = `
    <div class="cs-wizard" role="dialog" aria-label="Connect your calendar">
      <div class="cs-wizard-header">
        <h2 style="margin:0;font-size:var(--fs-md);font-weight:600;">Connect your calendar</h2>
        <button class="cs-wizard-close" aria-label="Close">&times;</button>
      </div>
      <div class="cs-wizard-body">
        <p class="settings-note" style="margin-bottom:18px;">
          We'll take you to Google or Microsoft to allow access. FitOps Pro shows that calendar's events on your calendar
          here, keeps those times off your booking pages, and adds the events, sessions and bookings you make here.
        </p>
        <div class="cs-wizard-providers">
          ${offer.map((p) => `
            <button class="cs-wizard-provider" data-provider="${p}">
              ${providerIcon(p)}
              <span style="font-weight:500;">${NAME[p]}</span>
              <span style="margin-left:auto;color:var(--text-muted);font-size:var(--fs-xs);">→</span>
            </button>`).join('')}
        </div>
        <div id="cs-wizard-status" style="margin-top:18px;font-size:var(--fs-xs);color:var(--text-muted);min-height:20px;"></div>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);
  const status = (t) => { const el = document.getElementById('cs-wizard-status'); if (el) el.textContent = t; };

  function close() {
    window.removeEventListener('message', onPostMsg);
    overlay.remove();
  }

  function onPostMsg(e) {
    if (e.data?.type !== 'revfit-calendar-connected') return;
    status('Connected. Setting up instant updates…');
    // Google's push is registered from here, awaited (the callback's own attempt does not reliably finish on Netlify).
    // Outlook's callback set up its own before it answered.
    const after = e.data?.provider === 'outlook'
      ? Promise.resolve()
      : callFunction('coach-calendar-watch-register', {}).catch((err) => console.warn('[calendar-sync] watch register failed:', err));
    after.finally(async () => { await loadStatus(); close(); });
  }
  window.addEventListener('message', onPostMsg);

  overlay.querySelector('.cs-wizard-close').addEventListener('click', close);
  overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });

  overlay.querySelectorAll('.cs-wizard-provider').forEach((btn) => btn.addEventListener('click', async () => {
    const p = btn.getAttribute('data-provider');
    status(p === 'outlook' ? 'Opening Microsoft…' : 'Opening Google…');
    try {
      const { url } = await callFunction(p === 'outlook' ? 'coach-calendar-outlook-start' : 'coach-calendar-oauth-start', {});
      const popup = window.open(url, 'revfit-calendar-oauth', 'width=540,height=720,top=100,left=100');
      if (!popup) { status('Popup blocked. Click Allow popups for this site, then try again.'); return; }
      // Backup detector — if the popup closes without sending its message, read the status again.
      const interval = setInterval(async () => {
        if (popup.closed) {
          clearInterval(interval);
          await loadStatus();
          if ((_status?.connections || []).find((c) => c.provider === p)) close();
          else status('Window closed before connection completed.');
        }
      }, 1000);
    } catch (e) {
      status(e.message || 'Couldn\'t start the connection.');
    }
  }));
}

// Detect ?calendarConnected=ok|error from the callback redirect (some
// browsers block window.opener.postMessage cross-origin).
function handleQueryFlag() {
  const params = new URLSearchParams(window.location.search);
  const v = params.get('calendarConnected');
  if (!v) return;
  if (v === 'ok') showToast('Calendar connected.', 'success');
  else if (v === 'error') showToast('Calendar connection failed.', 'error');
  // Strip from URL so refresh doesn't re-trigger
  const url = new URL(window.location.href);
  url.searchParams.delete('calendarConnected');
  window.history.replaceState({}, '', url.toString());
  loadStatus();
}

export async function initCalendarSync() {
  if (_initialized) {
    await loadStatus();
    return;
  }
  _initialized = true;
  handleQueryFlag();
  await loadStatus();
  // Refresh status periodically while the Sessions settings tab is open
  if (_pollTimer) clearInterval(_pollTimer);
  _pollTimer = setInterval(() => {
    const tabActive = document.getElementById('settings-tab-sessions')?.classList.contains('active');
    if (!tabActive) return;
    // A live Save button means the coach is mid-change; redrawing now would
    // throw that change away. Skip this tick — the next one runs once saved.
    if (document.getElementById('cs-save-prefs')?.disabled === false) return;
    loadStatus().catch(() => {});
  }, 30000);
}

window.initCalendarSync = initCalendarSync;
